<template>
  <LandingSection id="steps" :title="t('landing.steps.title')">
    <div class="landing-steps-grid">
      <div
        v-for="(step, index) in steps"
        :key="index"
        class="landing-step-card landing-card"
      >
        <div
          class="landing-step-index-badge text-white rounded-full flex items-center justify-center font-bold flex-shrink-0"
        >
          {{ index + 1 }}
        </div>
        <div class="landing-step-content">
          <h3 class="landing-step-title landing-card-title">
            {{ step.title }}
          </h3>
          <p class="landing-step-description leading-relaxed">
            {{ step.description }}
          </p>
        </div>
      </div>
    </div>
  </LandingSection>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import LandingSection from "@/components/landing/LandingSection.vue";
import { landingSteps as stepsSource } from "@/core/data/landingContent";

const { t } = useI18n();

const steps = computed(() =>
  stepsSource.map((step) => ({
    ...step,
    title: t(step.titleKey),
    description: t(step.descriptionKey),
  }))
);
</script>

<style scoped>
.landing-steps-grid {
  --step-line-size: 2rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--step-line-size);
  max-width: 72rem;
  margin: 0 auto;
  position: relative;
}

.landing-steps-grid::before,
.landing-steps-grid::after {
  content: "";
  position: absolute;
  pointer-events: none;
  opacity: 0.58;
}

.landing-steps-grid::before {
  top: 50%;
  left: 8%;
  right: 8%;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    color-mix(in srgb, var(--theme-gradient-start) 68%, transparent),
    color-mix(in srgb, var(--theme-gradient-end) 68%, transparent),
    transparent
  );
}

.landing-steps-grid::after {
  top: 8%;
  bottom: 8%;
  left: 50%;
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent,
    color-mix(in srgb, var(--theme-gradient-start) 58%, transparent),
    color-mix(in srgb, var(--theme-gradient-end) 58%, transparent),
    transparent
  );
}

.landing-step-index-badge {
  position: relative;
  z-index: 1;
  width: 4.25rem;
  height: 4.25rem;
  font-size: 1.625rem;
  line-height: 1;
  background-image: linear-gradient(
    to right,
    var(--theme-gradient-start),
    var(--theme-gradient-end)
  );
  box-shadow: 0 0 0 0.45rem
      color-mix(in srgb, var(--theme-gradient-start) 12%, transparent),
    0 0 1.5rem color-mix(in srgb, var(--theme-gradient-end) 32%, transparent);
}

.landing-step-card {
  position: relative;
  overflow: hidden;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  min-height: 13rem;
  background: radial-gradient(
      circle at top left,
      color-mix(in srgb, var(--theme-gradient-start) 12%, transparent),
      transparent 42%
    ),
    var(--surface-card);
}

.landing-step-card::after {
  content: "→";
  position: absolute;
  right: 1.5rem;
  bottom: 1rem;
  color: color-mix(in srgb, var(--theme-gradient-end) 40%, transparent);
  font-size: 2.5rem;
  line-height: 1;
}

.landing-step-card:nth-child(2)::after {
  content: "↙";
}

.landing-step-card:nth-child(3)::after {
  content: "→";
}

.landing-step-card:nth-child(4)::after {
  content: "✓";
}

.landing-step-content {
  text-align: left;
}

.landing-step-description {
  color: var(--text-secondary);
}

@media (max-width: 767px) {
  .landing-steps-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .landing-steps-grid::before {
    top: 2rem;
    bottom: 2rem;
    left: 50%;
    right: auto;
    width: 1px;
    height: auto;
    background: linear-gradient(
      to bottom,
      transparent,
      color-mix(in srgb, var(--theme-gradient-start) 58%, transparent),
      color-mix(in srgb, var(--theme-gradient-end) 58%, transparent),
      transparent
    );
  }

  .landing-steps-grid::after {
    display: none;
  }

  .landing-step-card {
    flex-direction: column;
    align-items: center;
    min-height: auto;
    padding: 1.75rem;
  }

  .landing-step-content {
    text-align: center;
  }

  .landing-step-card::after,
  .landing-step-card:nth-child(2)::after,
  .landing-step-card:nth-child(3)::after {
    content: "↓";
  }

  .landing-step-card:nth-child(4)::after {
    content: "✓";
  }
}
</style>
